import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faTrash, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useLang } from '../../../../language';
import { InputSelect } from '../../../../components/Inputs/InputSelect';
import { InputText } from '../../../../components/Inputs/InputText';
import { ModalStatusServer } from '../../../../components/ModalStatus/ModalStatusServer';
import { AutorizacionesServices } from '../../controllers/services/autorizaciones.services';
import { ModalNuevoUsuarioAutorizado } from './ModalNuevoUsuarioAutorizado';

// Listado de los usuarios autorizados en la empresa, con filtro por texto y por
// estado. El alta es directa (no se envía correo), por eso el botón es "Nuevo".
export const UsuariosAutorizados = () => {

    const Lang = useLang()

    const { empresaId } = useParams()

    const [openModal, setOpenModal] = useState(false)

    const [refreshKey, setRefreshKey] = useState(0)

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const [search, setSearch] = useState('')
    const [searchApplied, setSearchApplied] = useState('')

    const [estado, setEstado] = useState('')

    const [fetchUsuariosGET, fetchUsuariosGETData] = AutorizacionesServices.GET_USUARIOS_AUTORIZADOS()

    const [fetchAutorizarPOST, fetchAutorizarPOSTData] = AutorizacionesServices.POST_AUTORIZAR_USUARIO()

    const [fetchEliminarDELETE, fetchEliminarDELETEData] = AutorizacionesServices.DELETE_USUARIO_AUTORIZADO()

    // Autorización que se va a eliminar (se confirma en un Dialog)
    const [autorizacionAEliminar, setAutorizacionAEliminar] = useState<any>(null)

    useEffect(() => {

        if (!empresaId) return

        // GET /integra-web/empresas/usuarios-autorizados/{empresa_id}
        fetchUsuariosGET({
            id: Number(empresaId),
            params: {
                // El backend lee `page` (1-based), `per_page`, `search` y `estado`
                page: page + 1,
                per_page: rowsPerPage,
                search: searchApplied || undefined,
                estado: estado || undefined,
            }
        })

    }, [page, rowsPerPage, refreshKey, searchApplied, estado, empresaId])

    const handleInputSearch = (e: any) => {
        setSearch(e.target.value)
    }

    const handleChangeEstado = (e: any) => {
        setEstado(e.target.value)
        setPage(0)
    }

    const handleBuscar = (e?: any) => {

        if (e) e.preventDefault()

        setSearchApplied(search.trim())
        setPage(0)

    }

    const handleLimpiar = () => {

        setSearch('')
        setSearchApplied('')
        setEstado('')
        setPage(0)

    }

    const handleChangePage = (event: any, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleAutorizar = (usuario: any) => {

        // POST /integra-web/empresas/usuarios-autorizados
        fetchAutorizarPOST({
            body: {
                empresa_id: Number(empresaId),
                usuario_id: usuario.id,
            }
        })

    }

    // Tras el alta se cierra el modal y se recarga el listado.
    useEffect(() => {

        if (String(fetchAutorizarPOSTData.status)?.[0] === '2') {

            setOpenModal(false)
            setRefreshKey((key) => key + 1)

        }

    }, [fetchAutorizarPOSTData])

    const handleEliminar = (item: any) => {

        setAutorizacionAEliminar(null)

        // DELETE /integra-web/empresas/usuarios-autorizados/{id}
        // El id es el del registro de autorización, no el del usuario.
        fetchEliminarDELETE({ id: item.id })

    }

    useEffect(() => {

        if (String(fetchEliminarDELETEData.status)?.[0] === '2') {

            setAutorizacionAEliminar(null)
            setRefreshKey((key) => key + 1)

        }

    }, [fetchEliminarDELETEData])

    const estados = [
        { id: 'autorizada', name: Lang('AUTORIZADA') },
        { id: 'pendiente', name: Lang('PENDIENTE') },
        { id: 'rechazada', name: Lang('RECHAZADA') },
    ]

    return (
        <div>

            <Grid container spacing={2} alignItems='center' justifyContent='space-between'>

                <Grid item xs={12} sm={7}>
                    <Typography variant='h6' className="my-0 fw-500 color-primary">
                        {Lang('USUARIOS_AUTORIZADOS')}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={5}>
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Button
                            color="primary"
                            variant="contained"
                            size="medium"
                            startIcon={<FontAwesomeIcon icon={faUserPlus} />}
                            onClick={() => setOpenModal(true)}
                        >
                            {Lang('NUEVO')}
                        </Button>
                    </Box>
                </Grid>

            </Grid>

            <Box
                component="form"
                onSubmit={handleBuscar}
                className='mt-3'
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'flex-end' },
                    gap: { xs: 1.5, sm: 1 },
                }}
            >
                <Box sx={{ flex: '1 1 300px' }}>
                    <InputText
                        label={Lang('BUSCAR')}
                        name='search'
                        value={search}
                        onChange={handleInputSearch}
                    />
                </Box>

                <Box sx={{ flex: '0 1 220px' }}>
                    <InputSelect
                        label={Lang('ESTADO')}
                        name='estado'
                        value={estado}
                        values={estados}
                        onChange={handleChangeEstado}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        size="small"
                        disabled={fetchUsuariosGETData.loading}
                    >
                        {Lang('REALIZAR_BUSQUEDA')}
                    </Button>

                    <Button
                        type="button"
                        color="primary"
                        variant="outlined"
                        size="small"
                        onClick={handleLimpiar}
                    >
                        {Lang('LIMPIAR')}
                    </Button>
                </Box>
            </Box>

            {
                !fetchUsuariosGETData.loading && fetchUsuariosGETData.error &&
                <Typography variant='caption' className='my-0 mt-3' sx={{ color: 'error.main', display: 'block', textAlign: 'center' }}>
                    {String(fetchUsuariosGETData.error?.error || fetchUsuariosGETData.error?.message || fetchUsuariosGETData.error)}
                </Typography>
            }

            <TableContainer className='mt-3'>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{Lang('NOMBRE')}</TableCell>
                            <TableCell>{Lang('NIF')}</TableCell>
                            <TableCell>{Lang('EMAIL')}</TableCell>
                            <TableCell>{Lang('ESTADO')}</TableCell>
                            <TableCell>{Lang('FECHA')}</TableCell>
                            <TableCell align="right">{Lang('ACCIONES')}</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {
                            fetchUsuariosGETData.loading &&

                            Array.from({ length: 5 }).map((_, index) => (
                                <TableRow key={`skeleton-${index}`}>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell width={160}><Skeleton variant="rounded" height={32} /></TableCell>
                                    <TableCell><Skeleton variant="text" /></TableCell>
                                    <TableCell align="right" width={90}><Skeleton variant="rounded" height={32} /></TableCell>
                                </TableRow>
                            ))
                        }

                        {
                            fetchUsuariosGETData.data?.items?.map((item: any) => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.nombre || item.username}</TableCell>
                                    <TableCell>{item.nif}</TableCell>
                                    <TableCell>{item.email}</TableCell>
                                    <TableCell>
                                        {
                                            item.estado === 'autorizada' ?
                                                <Chip
                                                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                                                    label={Lang('AUTORIZADA')}
                                                    color="success"
                                                /> :
                                                item.estado === 'rechazada' ?
                                                <Chip
                                                    icon={<FontAwesomeIcon icon={faTimesCircle} />}
                                                    label={Lang('RECHAZADA')}
                                                    color="error"
                                                /> :
                                                <Chip
                                                    label={Lang('PENDIENTE')}
                                                    color="warning"
                                                />
                                        }
                                    </TableCell>
                                    <TableCell>{item.decision_at ? new Date(item.decision_at).toLocaleDateString() : '-'}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            startIcon={<FontAwesomeIcon icon={faTrash} />}
                                            onClick={() => setAutorizacionAEliminar(item)}
                                        >
                                            {Lang('ELIMINAR')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        }

                        {
                            !fetchUsuariosGETData.loading && fetchUsuariosGETData.data?.items?.length === 0 &&

                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Box sx={{ textAlign: 'center', py: 5 }}>
                                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                            {Lang('SIN_RESULTADOS')}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={fetchUsuariosGETData.data?.numeroTotalElementos || 0}
                page={fetchUsuariosGETData.data?.numeroPagina || 0}
                onPageChange={handleChangePage}
                rowsPerPage={fetchUsuariosGETData.data?.numeroElementos || rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={Lang('FILAS_POR_PAGINA')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${Lang('DE')} ${count}`}
            />

            <ModalNuevoUsuarioAutorizado
                open={openModal}
                onClose={() => setOpenModal(false)}
                onAutorizar={handleAutorizar}
                empresaId={Number(empresaId)}
                guardando={!!fetchAutorizarPOSTData.loading}
            />

            <ModalStatusServer
                fetchData={fetchAutorizarPOSTData}
                activeLoading
                messageStatus={
                    {
                        200:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchAutorizarPOSTData.data?.message || Lang('USUARIO_AUTORIZADO')}
                                </Typography>
                            </>,
                        201:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchAutorizarPOSTData.data?.message || Lang('USUARIO_AUTORIZADO')}
                                </Typography>
                            </>,
                        403:
                            <>
                                <FontAwesomeIcon icon={faTimesCircle} className='color-red fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchAutorizarPOSTData.error?.error || Lang('SIN_ACCESO_EMPRESA')}
                                </Typography>
                            </>,
                        422:
                            <>
                                <FontAwesomeIcon icon={faTimesCircle} className='color-red fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {Object.values(fetchAutorizarPOSTData.error?.errors || {}).flat()[0] || Lang('ERROR_AUTORIZAR_USUARIO')}
                                </Typography>
                            </>,
                    }
                }
            />

            <Dialog
                open={!!autorizacionAEliminar}
                onClose={() => setAutorizacionAEliminar(null)}
            >
                <DialogTitle>
                    {Lang('ELIMINAR_USUARIO_AUTORIZADO')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {Lang('ELIMINAR_USUARIO_AUTORIZADO_CONFIRMACION')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setAutorizacionAEliminar(null)}>
                        {Lang('CANCELAR')}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => handleEliminar(autorizacionAEliminar)}
                    >
                        {Lang('ELIMINAR')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ModalStatusServer
                fetchData={fetchEliminarDELETEData}
                activeLoading
                messageStatus={
                    {
                        200:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchEliminarDELETEData.data?.message || Lang('USUARIO_AUTORIZADO_ELIMINADO')}
                                </Typography>
                            </>,
                        201:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchEliminarDELETEData.data?.message || Lang('USUARIO_AUTORIZADO_ELIMINADO')}
                                </Typography>
                            </>,
                        403:
                            <>
                                <FontAwesomeIcon icon={faTimesCircle} className='color-red fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchEliminarDELETEData.error?.error || Lang('SIN_ACCESO_EMPRESA')}
                                </Typography>
                            </>,
                    }
                }
            />

        </div>
    )
}
